import React, { useRef } from 'react';

import { useScrollIndicator } from './hooks/useScrollIndicator';
import { EmptyTable, TableHeader, TableRow, TableSkeleton } from './components';
import type { DataTableProps, TableRow as TableRowType } from './types';

export * from './types';

export function DataTable<T = TableRowType>({
  data,
  columns,
  toolbar,
  isLoading = false,
  emptyMessage = 'No data found',
  loadingRows = 3,
  onRowClick,
  className = '',
  enableHorizontalScroll = false
}: DataTableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showLeftShadow, showRightShadow } = useScrollIndicator(scrollRef);

  if (isLoading) {
    return (
      <TableSkeleton columns={columns} rows={loadingRows} toolbar={toolbar} />
    );
  }

  if (data.length === 0) {
    return (
      <EmptyTable columns={columns} message={emptyMessage} toolbar={toolbar} />
    );
  }

  // Modo com scroll horizontal (variant para Report)
  if (enableHorizontalScroll) {
    return (
      <div className={`w-full relative ${className}`}>
        {/* Left shadow indicator */}
        {showLeftShadow && (
          <div
            className="absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-20"
            style={{
              background:
                'linear-gradient(to right, rgba(0,0,0,0.15), transparent)'
            }}
          />
        )}

        {/* Scrollable container */}
        <div
          ref={scrollRef}
          className="overflow-x-auto scroll-smooth border border-[#EEEFF2] rounded-lg"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#CBD5E1 #F1F5F9'
          }}>
          {toolbar}
          <TableHeader columns={columns} enableStickyColumns={true} />
          <div className="bg-white">
            {data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                row={row}
                columns={columns}
                rowIndex={rowIndex}
                onRowClick={onRowClick}
                enableStickyColumns={true}
              />
            ))}
          </div>
        </div>

        {/* Right shadow indicator */}
        {showRightShadow && (
          <div
            className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-20"
            style={{
              background:
                'linear-gradient(to left, rgba(0,0,0,0.15), transparent)'
            }}
          />
        )}

        {/* Custom scrollbar styles for webkit browsers */}
        <style jsx>{`
          div::-webkit-scrollbar {
            height: 8px;
          }
          div::-webkit-scrollbar-track {
            background: #f1f5f9;
          }
          div::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}</style>
      </div>
    );
  }

  // Modo padrão (sem scroll horizontal - comportamento original)
  return (
    <div
      className={`w-full border border-[#EEEFF2] rounded-lg overflow-hidden ${className}`}>
      {toolbar}
      <TableHeader columns={columns} enableStickyColumns={false} />
      <div className="bg-white">
        {data.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            row={row}
            columns={columns}
            rowIndex={rowIndex}
            onRowClick={onRowClick}
            enableStickyColumns={false}
          />
        ))}
      </div>
    </div>
  );
}
